import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowUpRight, Droplet, Square as SquareIcon, Undo2, X, Check } from 'lucide-react';

// Canvas annotator for captured desktop screenshots (IT tickets). Marks are
// plain drawings in image coordinates — a screenshot of Outlook has no DOM to
// anchor to, so unlike the website widget these are simple vectors flattened
// into a JPEG on attach. Tools mirror the website widget (arrow, box, text)
// plus Blur for sensitive regions. Box and arrow can carry an optional note,
// which renders as a numbered label — so "point at it and say what's wrong"
// works the same way people already know from the website tool.

type Tool = 'arrow' | 'box' | 'blur';
type Mark =
  | { type: 'arrow' | 'box'; x: number; y: number; w: number; h: number; note?: string }
  | { type: 'blur'; x: number; y: number; w: number; h: number }
  | { type: 'text'; x: number; y: number; text: string };

const BLUE = '#17479e';
const AMBER = '#ffaa00';
const NOTE_MAX = 300;

function normRect(m: { x: number; y: number; w: number; h: number }) {
  return { x: Math.min(m.x, m.x + m.w), y: Math.min(m.y, m.y + m.h), w: Math.abs(m.w), h: Math.abs(m.h) };
}
function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
function badge(ctx: CanvasRenderingContext2D, x: number, y: number, n: number, s: number) {
  ctx.save();
  ctx.fillStyle = AMBER;
  roundRect(ctx, x - s / 2, y - s / 2, s, s, s * 0.28);
  ctx.fill();
  ctx.fillStyle = '#3a2a00';
  ctx.font = `800 ${s * 0.62}px "Helvetica Neue", Helvetica, Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(n), x, y + 0.5);
  ctx.restore();
}
function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  let cur = '';
  for (const word of text.split(/\s+/)) {
    // Hard-break a token wider than the box (e.g. a long URL with no spaces).
    if (ctx.measureText(word).width > maxWidth) {
      if (cur) { lines.push(cur); cur = ''; }
      let chunk = '';
      for (const ch of word) {
        if (chunk && ctx.measureText(chunk + ch).width > maxWidth) { lines.push(chunk); chunk = ch; }
        else chunk += ch;
      }
      cur = chunk;
      continue;
    }
    const t = cur ? `${cur} ${word}` : word;
    if (cur && ctx.measureText(t).width > maxWidth) { lines.push(cur); cur = word; }
    else cur = t;
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [text];
}

// Wraps long notes and clamps the box inside the image (logicalW × logicalH)
// so a label never runs off the edge.
function label(ctx: CanvasRenderingContext2D, x: number, y: number, text: string, fs: number, logicalW: number, logicalH: number) {
  ctx.font = `600 ${fs}px "Helvetica Neue", Helvetica, Arial, sans-serif`;
  const margin = fs * 0.5, padX = fs * 0.55, padY = fs * 0.4, lh = fs * 1.28;
  const maxBoxW = Math.max(90, Math.min(logicalW - margin * 2, logicalW * 0.6, 420));
  const lines = wrapText(ctx, text, maxBoxW - padX * 2);
  const boxW = Math.min(maxBoxW, Math.max(...lines.map((l) => ctx.measureText(l).width)) + padX * 2);
  const boxH = lines.length * lh + padY * 2;
  const bx = Math.max(margin, Math.min(x, logicalW - boxW - margin));
  const by = Math.max(margin, Math.min(y, logicalH - boxH - margin));
  ctx.save();
  ctx.shadowColor = 'rgba(17,28,78,.35)';
  ctx.shadowBlur = 6;
  ctx.fillStyle = BLUE;
  roundRect(ctx, bx, by, boxW, boxH, 7);
  ctx.fill();
  ctx.restore();
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  lines.forEach((line, i) => ctx.fillText(line, bx + padX, by + padY + fs * 0.82 + i * lh));
}

function drawMarks(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  marks: Mark[],
  draft: Mark | null,
  scale: number,
) {
  const logicalW = img.naturalWidth * scale;
  const logicalH = img.naturalHeight * scale;
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.drawImage(img, 0, 0, logicalW, logicalH);
  const all = draft ? [...marks, draft] : marks;
  let n = 0;
  const fs = Math.max(13, 15 * scale);

  for (const m of all) {
    if (m.type === 'blur') {
      const { x, y, w, h } = normRect(m);
      if (w < 4 || h < 4) continue;
      const off = document.createElement('canvas');
      const px = Math.max(2, Math.round(Math.min(w, h) / 7));
      off.width = Math.max(1, Math.round(w / px));
      off.height = Math.max(1, Math.round(h / px));
      off.getContext('2d')!.drawImage(img, x / scale, y / scale, w / scale, h / scale, 0, 0, off.width, off.height);
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(off, 0, 0, off.width, off.height, x, y, w, h);
      ctx.imageSmoothingEnabled = true;
      continue;
    }
    if (m.type === 'text') {
      label(ctx, m.x, m.y, m.text, fs, logicalW, logicalH);
      continue;
    }

    // box / arrow
    ctx.lineWidth = Math.max(2.5, 3 * scale);
    ctx.strokeStyle = BLUE;
    ctx.fillStyle = BLUE;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.save();
    ctx.shadowColor = 'rgba(255,255,255,.8)';
    ctx.shadowBlur = 3;
    let bx = m.x, by = m.y;
    let labelX = m.x, labelBottom = m.y;
    if (m.type === 'box') {
      const r = normRect(m);
      ctx.strokeRect(r.x, r.y, r.w, r.h);
      bx = r.x; by = r.y;
      labelX = r.x; labelBottom = r.y + r.h;
    } else {
      const x2 = m.x + m.w, y2 = m.y + m.h;
      const ang = Math.atan2(y2 - m.y, x2 - m.x);
      const head = Math.max(10, 14 * scale);
      ctx.beginPath();
      ctx.moveTo(m.x, m.y);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - head * Math.cos(ang - 0.45), y2 - head * Math.sin(ang - 0.45));
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - head * Math.cos(ang + 0.45), y2 - head * Math.sin(ang + 0.45));
      ctx.stroke();
      labelX = Math.min(m.x, x2); labelBottom = Math.max(m.y, y2);
    }
    ctx.restore();
    n++;
    const bsz = Math.max(16, 18 * scale);
    badge(ctx, bx, by, n, bsz);
    // Note label hangs just below the mark (clamped inside the image).
    if (m.note) label(ctx, labelX, labelBottom + bsz * 0.5, `${n}. ${m.note}`, fs, logicalW, logicalH);
  }
}

/** Flatten image + marks at natural resolution into a high-quality JPEG. */
function flatten(img: HTMLImageElement, marks: Mark[]): string {
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  drawMarks(canvas.getContext('2d')!, img, marks, null, 1);
  // Prefer quality; only step down if a screenshot is unusually large.
  for (const q of [0.94, 0.88, 0.8, 0.7]) {
    const url = canvas.toDataURL('image/jpeg', q);
    if (url.length <= 2_200_000) return url;
  }
  return canvas.toDataURL('image/jpeg', 0.6);
}

export function ScreenshotAnnotator({
  imageDataUrl,
  onAttach,
  onRetake,
  onCancel,
}: {
  imageDataUrl: string;
  onAttach: (jpegDataUrl: string, markCount: number) => void;
  onRetake: () => void;
  onCancel: () => void;
}) {
  const [tool, setTool] = useState<Tool>('arrow');
  const [marks, setMarks] = useState<Mark[]>([]);
  const [draft, setDraft] = useState<Mark | null>(null);
  // Note editor: which mark index is being annotated, and where to float the input.
  const [noteFor, setNoteFor] = useState<{ index: number; x: number; y: number; isText: boolean } | null>(null);
  const [noteValue, setNoteValue] = useState('');
  const [img, setImg] = useState<HTMLImageElement | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ x: number; y: number } | null>(null);
  const scaleRef = useRef(1);

  useEffect(() => {
    const image = new Image();
    image.onload = () => setImg(image);
    image.src = imageDataUrl;
  }, [imageDataUrl]);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap || !img) return;
    const scale = Math.min(1, wrap.clientWidth / img.naturalWidth);
    scaleRef.current = scale;
    // Backing store at device-pixel-ratio for a crisp preview on hi-DPI
    // screens; CSS display size unchanged, marks stay in display-pixel space.
    const dpr = window.devicePixelRatio || 1;
    const dispW = img.naturalWidth * scale, dispH = img.naturalHeight * scale;
    canvas.style.width = `${dispW}px`;
    canvas.style.height = `${dispH}px`;
    canvas.width = Math.round(dispW * dpr);
    canvas.height = Math.round(dispH * dpr);
    const ctx = canvas.getContext('2d')!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawMarks(ctx, img, marks, draft, scale);
  }, [img, marks, draft]);

  useEffect(() => { redraw(); }, [redraw]);
  useEffect(() => {
    window.addEventListener('resize', redraw);
    return () => window.removeEventListener('resize', redraw);
  }, [redraw]);

  function xy(e: React.PointerEvent): { x: number; y: number } {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  function onPointerDown(e: React.PointerEvent) {
    if (!img || noteFor) return;
    const p = xy(e);
    dragRef.current = p;
    setDraft({ type: tool, x: p.x, y: p.y, w: 0, h: 0 });
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onPointerMove(e: React.PointerEvent) {
    if (!dragRef.current) return;
    const p = xy(e);
    setDraft((d) => (d && d.type !== 'text' ? { ...d, w: p.x - dragRef.current!.x, h: p.y - dragRef.current!.y } : d));
  }
  function onPointerUp() {
    if (!dragRef.current) return;
    dragRef.current = null;
    setDraft((d) => {
      if (d && d.type !== 'text' && (Math.abs(d.w) > 6 || Math.abs(d.h) > 6)) {
        setMarks((m) => {
          const idx = m.length;
          // Box/arrow (not blur) get an optional note editor near the mark.
          if (d.type !== 'blur') {
            const r = normRect(d);
            setNoteFor({ index: idx, x: r.x, y: r.y, isText: false });
            setNoteValue('');
          }
          return [...m, d];
        });
      }
      return null;
    });
  }

  function commitNote() {
    if (!noteFor) return;
    const { index, isText } = noteFor;
    const val = noteValue.trim().slice(0, NOTE_MAX);
    setMarks((m) =>
      m
        .map((mk, i) => {
          if (i !== index) return mk;
          if (mk.type === 'text') return { ...mk, text: val };
          return { ...mk, note: val || undefined };
        })
        // A text mark with no text is meaningless — drop it.
        .filter((mk) => !(mk.type === 'text' && !mk.text)),
    );
    setNoteFor(null);
    setNoteValue('');
    void isText;
  }

  function attach() {
    if (!img) return;
    const scale = scaleRef.current;
    const natural = marks.map((m) =>
      m.type === 'text'
        ? { ...m, x: m.x / scale, y: m.y / scale }
        : { ...m, x: m.x / scale, y: m.y / scale, w: m.w / scale, h: m.h / scale },
    );
    onAttach(flatten(img, natural as Mark[]), marks.length);
  }

  const toolBtn = (t: Tool, lbl: string, Icon: typeof ArrowUpRight) => (
    <button
      type="button"
      onClick={() => setTool(t)}
      className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
        tool === t ? 'bg-primary text-white' : 'text-gray-500 hover:bg-gray-100'
      }`}
    >
      <Icon className="w-3.5 h-3.5" /> {lbl}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/70 p-3 sm:p-6">
      <div className="flex max-h-[94vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <p className="mr-auto text-sm font-semibold text-gray-900">
            Annotate your screenshot
            <span className="ml-2 hidden font-normal text-gray-500 sm:inline">
              draw on it, add a note, blur anything sensitive
            </span>
          </p>
          <button type="button" onClick={onRetake} className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50">
            Retake
          </button>
          <button type="button" onClick={onCancel} className="rounded-lg border border-gray-300 px-2 py-1.5 text-gray-500 hover:bg-gray-50" aria-label="Cancel">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div ref={wrapRef} className="relative flex-1 overflow-auto bg-gray-100 p-3">
          <div className="relative inline-block">
            <canvas
              ref={canvasRef}
              className="block touch-none rounded-md shadow-sm"
              style={{ cursor: 'crosshair' }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
            />
            {noteFor && (
              <div
                className="absolute z-10 w-60"
                style={{
                  left: Math.min(noteFor.x, (canvasRef.current?.clientWidth ?? 300) - 248),
                  top: noteFor.y + 22,
                }}
              >
                <input
                  autoFocus
                  maxLength={NOTE_MAX}
                  value={noteValue}
                  onChange={(e) => setNoteValue(e.target.value)}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter') commitNote();
                    if (e.key === 'Escape') commitNote();
                  }}
                  onBlur={commitNote}
                  placeholder={`Add a note (optional), Enter · max ${NOTE_MAX}`}
                  className="w-full rounded-md border-2 border-primary bg-white px-2 py-1 text-sm shadow-lg"
                />
                <div className={`mt-1 text-right text-[11px] font-semibold drop-shadow ${noteValue.length >= NOTE_MAX ? 'text-amber-500' : 'text-gray-200'}`}>
                  {noteValue.length}/{NOTE_MAX}{noteValue.length >= NOTE_MAX ? ' · limit reached' : ''}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1 border-t border-border px-4 py-3">
          {toolBtn('arrow', 'Arrow', ArrowUpRight)}
          {toolBtn('box', 'Box', SquareIcon)}
          {toolBtn('blur', 'Blur', Droplet)}
          <span className="ml-2 hidden text-xs text-muted md:inline">
            Draw a box or arrow, then type a note (up to {NOTE_MAX} chars).
          </span>
          <button
            type="button"
            onClick={() => setMarks((m) => m.slice(0, -1))}
            disabled={marks.length === 0}
            className="ml-auto flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-500 hover:bg-gray-100 disabled:opacity-40"
          >
            <Undo2 className="w-3.5 h-3.5" /> Undo{marks.length > 0 ? ` (${marks.length})` : ''}
          </button>
          <button
            type="button"
            onClick={attach}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-xs font-bold text-white hover:opacity-90"
          >
            <Check className="w-3.5 h-3.5" /> Attach to ticket
          </button>
        </div>
      </div>
    </div>
  );
}
